const normalizeName = (value) => (value || '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .replace(/^\s+|\s+$/g, '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const normalizeCouncil = (council) => (council || '').replace(/\D+/g, '');

// Both referral and requesting doctor are free text typed by whoever fills the
// form, so the aggregated lists dedupe on a normalized name. A requesting doctor
// also carries a council (CRM), the only stable id an external doctor has, so it
// wins over the name - a name fixed after the fact still lands on the entry the
// council already created. A referral has no such key.
const isSameEntry = (a, b) => {
  const councilA = normalizeCouncil(a && a.council);
  const councilB = normalizeCouncil(b && b.council);
  if (councilA && councilB) {
    return councilA === councilB;
  }
  const nameA = normalizeName(a && a.value);
  return !!nameA && nameA === normalizeName(b && b.value);
};

const findEntry = (list, entry) => {
  const council = normalizeCouncil(entry && entry.council);
  if (council) {
    const byCouncil = (list || []).find((el) => normalizeCouncil(el.council) === council);
    if (byCouncil) {
      return byCouncil;
    }
  }
  const name = normalizeName(entry && entry.value);
  if (!name) {
    return undefined;
  }
  return (list || []).find((el) => normalizeName(el.value) === name);
};

// Referral doubles as the source picked from the fixed radio options, so text
// that means one of them is stored under its canonical spelling.
const canonicalValue = (value, canonicalValues) => {
  const name = normalizeName(value);
  const found = (canonicalValues || []).find((el) => normalizeName(el) === name);
  return found || value;
};

function levenshtein(a, b) {
  if (a === b) {
    return 0;
  }
  if (!a.length || !b.length) {
    return Math.max(a.length, b.length);
  }
  let prev = Array.from({ length: b.length + 1 }, (_unused, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const curr = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = curr;
  }
  return prev[b.length];
}

// Closest entry to a typed name, when it is close enough to be a typo.
// Only ever used to suggest - never to merge automatically.
const suggestEntry = (list, entry, threshold = 0.85) => {
  if (findEntry(list, entry)) {
    return undefined;
  }
  const typed = normalizeName(entry && entry.value);
  if (typed.length < 5) {
    return undefined;
  }
  let best;
  let bestScore = threshold;
  (list || []).forEach((el) => {
    const candidate = normalizeName(el.value);
    if (!candidate) {
      return;
    }
    const score = 1 - (levenshtein(typed, candidate) / Math.max(typed.length, candidate.length));
    if (score > bestScore) {
      best = el;
      bestScore = score;
    }
  });
  return best;
};

// The referral radio writes these into the same field as the free text.
const REFERRAL_CANONICAL_VALUES = ['Facebook', 'Instagram', 'WhatsApp'];

export {
  REFERRAL_CANONICAL_VALUES,
  normalizeName,
  normalizeCouncil,
  isSameEntry,
  findEntry,
  canonicalValue,
  suggestEntry,
};
